import { useRef, useState } from "react";
import classes from "./style.module.scss";
import banner_lady from "../../asset/login_image_lady.png";
import modusLogo from "../../asset/Modus_logo.png";

/**
 * Shared shell for every auth screen (login, OTP, reset password) —
 * the teal hero panel with the circular portrait crop on the left,
 * the white form panel on the right. Previously this markup/CSS was
 * copy-pasted three times (View/Auth/style.module.scss,
 * View/Auth/UserLogin/style.module.scss and
 * Components/confirm-otp/style.module.scss) and had already drifted
 * out of sync between copies — one of them didn't even define the
 * scrim class its own JSX rendered. One shared shell means the three
 * screens can no longer visually diverge, and a fix here reaches all
 * three instead of needing to be repeated three times.
 *
 * `heading` is a node so callers can control line breaks (each screen
 * uses different hero copy); `children` is everything on the white
 * side below the logo — each screen owns its own form/state markup.
 */
export default function AuthShell({
  eyebrow = "MODUS CLINICAL INTELLIGENCE",
  heading,
  children,
}) {
  // Subtle 3D tilt on the hero, following the cursor — carried over
  // from the previous design. Purely cosmetic, resets on mouse leave,
  // and does nothing on touch devices (no mousemove there). Lives here
  // instead of in each page component so all three screens get it for
  // free, the same reason the rest of this shell is shared.
  const heroRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: py * -6, y: px * 8 });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div className={classes["page"]}>
      <main className={classes["shell"]}>
        <section
          className={classes["hero"]}
          ref={heroRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            transform: `perspective(1400px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          }}
        >
          <p className={classes["eyebrow"]}>{eyebrow}</p>
          <h2 className={classes["headline"]}>{heading}</h2>
          <span className={classes["ring"]} aria-hidden="true" />
          <div className={classes["portrait"]}>
            <img src={banner_lady} alt="Clinician" />
          </div>
        </section>

        <section className={classes["form-side"]}>
          <img className={classes["logo"]} src={modusLogo} alt="Modus" />
          {children}
        </section>
      </main>

      <p className={classes["page-footer"]}>
        © {new Date().getFullYear()} Modus Clinical Intelligence · All
        rights reserved
      </p>
    </div>
  );
}
